/**
 * ResourceRequestsTab
 *
 * World-class table-based view for inbound resource requests.
 * Table for scanning/triage → expandable row detail → Plan Drawer.
 * Evo Design System — semantic tokens only, no hardcoded colours.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useRequestPriorityThresholds,
  computeRequestPriority,
} from '@/hooks/useRequestPriorityThresholds';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Search,
  Timer,
  CalendarDays,
  Tag,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Inbox,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  MapPin,
  MessageSquare,
  FileSpreadsheet,
  Settings2,
  GripVertical,
  RotateCcw,
  Lock,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ResourceRequest, PlanScenario, PlanPreviewScenario, RequestPriority } from '@/types/resourceRequest';
import { Resource, Booking, WorkingHoursProfile } from '@/types';
import { generatePlanScenarios } from '@/lib/resourceRequestPlanner';
import { ResourceRequestPlanDrawer } from './ResourceRequestPlanDrawer';
import { toast } from 'sonner';
import { demandsApi, projectsApi, ApiError } from '@/services/api';
import type { Demand } from '@/types/demand';
import type { EBProjectsBudgetComposite } from '@/services/api';
import { demandsToResourceRequests, findDemandLineByMonth } from '@/lib/demandAdapter';
import { useCompany } from '@/contexts/CompanyContext';
import { useDataMode } from '@/contexts/DataModeContext';

import { getDemoResourceRequests } from '@/data/demoDemands';
import { Loader2, BarChart3 } from 'lucide-react';
import { findRoleBudget, budgetHealthAccent } from '@/hooks/useProjectBudget';

// ── Types ─────────────────────────────────────────────────────────────────────

type StatusFilter   = 'all' | ResourceRequest['status'];
type PriorityFilter = 'all' | RequestPriority;
type SortField      = 'priority' | 'project' | 'role' | 'hours' | 'startDate' | 'received' | 'status';
type SortDir        = 'asc' | 'desc' | 'none';

// ── Column configuration ───────────────────────────────────────────────────────

export type RRColumnKey = 'priority' | 'project' | 'role' | 'hours' | 'startDate' | 'status';
type RRColumnCategory = 'Core' | 'Details';

interface RRColumnDef {
  key: RRColumnKey;
  label: string;
  description: string;
  category: RRColumnCategory;
  defaultVisible: boolean;
  locked?: boolean;
  sortField?: SortField;
}

const RR_ALL_COLUMNS: RRColumnDef[] = [
  { key: 'priority',  label: 'Priority',   description: 'Request urgency and priority level',    category: 'Core',    defaultVisible: true, locked: true,  sortField: 'priority'  },
  { key: 'project',   label: 'Project',    description: 'Project name and code',                 category: 'Core',    defaultVisible: true, locked: true,  sortField: 'project'   },
  { key: 'role',      label: 'Role',       description: 'Required role for the request',          category: 'Details', defaultVisible: true,               sortField: 'role'      },
  { key: 'hours',     label: 'Hours',      description: 'Total hours requested',                 category: 'Details', defaultVisible: true,               sortField: 'hours'     },
  { key: 'startDate', label: 'Start Date', description: 'Requested start date',                  category: 'Core',    defaultVisible: true,               sortField: 'startDate' },
  { key: 'status',    label: 'Status',     description: 'Current request status',                category: 'Core',    defaultVisible: true,               sortField: 'status'    },
];

const RR_DEFAULT_VISIBLE = new Set<RRColumnKey>(RR_ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key));
const RR_DEFAULT_ORDER   = RR_ALL_COLUMNS.map(c => c.key);

// ── Config (EDS tokens — no hardcoded colours) ─────────────────────────────────

const STATUS_CONFIG: Record<ResourceRequest['status'], {
  label: string;
  accent: 'teal' | 'warning' | 'info' | 'destructive';
  icon: React.ReactNode;
}> = {
  'pending':    { label: 'Pending',    accent: 'warning',     icon: <Timer       className="h-3 w-3" /> },
  'plan-ready': { label: 'Plan Ready', accent: 'info',        icon: <CheckCircle2 className="h-3 w-3" /> },
  'accepted':   { label: 'Accepted',   accent: 'teal',        icon: <CheckCircle2 className="h-3 w-3" /> },
  'declined':   { label: 'Declined',   accent: 'destructive', icon: <XCircle     className="h-3 w-3" /> },
};

const PRIORITY_CONFIG: Record<RequestPriority, {
  label: string;
  dotAccent: string;           // inline style key into EDS accent palette
  badgeAccent: 'destructive' | 'warning' | 'info' | 'teal';
  rowAccent: string;           // subtle left-border on expanded row
  order: number;
}> = {
  critical: { label: 'Critical', dotAccent: 'accent-destructive', badgeAccent: 'destructive', rowAccent: 'border-l-[var(--accent-destructive-500)]', order: 0 },
  high:     { label: 'High',     dotAccent: 'accent-warning',     badgeAccent: 'warning',     rowAccent: 'border-l-[var(--accent-warning-500)]',     order: 1 },
  medium:   { label: 'Medium',   dotAccent: 'accent-info',        badgeAccent: 'info',        rowAccent: 'border-l-[var(--accent-info-500)]',        order: 2 },
  low:      { label: 'Low',      dotAccent: 'accent-teal',        badgeAccent: 'teal',        rowAccent: 'border-l-[var(--accent-teal-500)]',        order: 3 },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return format(parseISO(iso), 'd MMM yy');
}

function hoursLabel(h: number) {
  if (h % 8 === 0) return `${h}h / ${h / 8}d`;
  return `${h}h`;
}



function sortRequests(
  requests: ResourceRequest[],
  field: SortField,
  dir: SortDir,
): ResourceRequest[] {
  if (dir === 'none') return requests;
  const mul = dir === 'asc' ? 1 : -1;
  return [...requests].sort((a, b) => {
    switch (field) {
      case 'priority':   return mul * (PRIORITY_CONFIG[a.priority].order - PRIORITY_CONFIG[b.priority].order);
      case 'project':    return mul * a.projectName.localeCompare(b.projectName);
      case 'role':       return mul * a.role.localeCompare(b.role);
      case 'hours':      return mul * (a.totalHoursRequested - b.totalHoursRequested);
      case 'startDate':  return mul * (parseISO(a.requestedStartDate).getTime() - parseISO(b.requestedStartDate).getTime());
      case 'received':   return mul * (parseISO(a.receivedAt).getTime() - parseISO(b.receivedAt).getTime());
      case 'status': {
        const so: Record<ResourceRequest['status'], number> = { pending: 0, 'plan-ready': 1, accepted: 2, declined: 3 };
        return mul * (so[a.status] - so[b.status]);
      }
      default: return 0;
    }
  });
}

// ── SortableHeader ─────────────────────────────────────────────────────────────

interface SortableHeadProps {
  field: SortField;
  label: string;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHead({ field, label, currentField, currentDir, onSort, className }: SortableHeadProps) {
  const active = currentField === field && currentDir !== 'none';
  return (
    <TableHead className={cn('cursor-pointer select-none group', className)} onClick={() => onSort(field)}>
      <div className="flex items-center gap-1">
        <span className={cn(
          'transition-colors',
          active ? 'text-foreground font-semibold' : 'text-muted-foreground group-hover:text-foreground',
        )}>
          {label}
        </span>
        {active ? (
          currentDir === 'asc'
            ? <ArrowUp   className="h-3 w-3 text-primary shrink-0" />
            : <ArrowDown className="h-3 w-3 text-primary shrink-0" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
        )}
      </div>
    </TableHead>
  );
}

// ── Sortable column row (drag-and-drop) for column configurator ───────────────

interface RRSortableColumnRowProps {
  colDef: RRColumnDef;
  isVisible: boolean;
  onToggle: (key: RRColumnKey) => void;
}

function RRSortableColumnRow({ colDef, isVisible, onToggle }: RRSortableColumnRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: colDef.key });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors duration-150',
        isDragging
          ? 'bg-accent border-primary/40 shadow-lg cursor-grabbing'
          : isVisible && !colDef.locked
            ? 'bg-card border-border hover:border-border/80 hover:bg-accent/30'
            : isVisible && colDef.locked
              ? 'bg-muted/20 border-border/40'
              : 'bg-muted/10 border-border/30',
      )}
    >
      {/* Drag handle */}
      <button
        className={cn(
          'shrink-0 text-muted-foreground/30 transition-colors touch-none',
          !colDef.locked && 'hover:text-muted-foreground cursor-grab active:cursor-grabbing',
          colDef.locked && 'cursor-default opacity-30',
        )}
        {...(!colDef.locked ? { ...attributes, ...listeners } : {})}
        aria-label={`Drag to reorder ${colDef.label}`}
        tabIndex={colDef.locked ? -1 : 0}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium leading-snug', !isVisible ? 'text-muted-foreground' : 'text-foreground')}>
          {colDef.label}
        </p>
        <p className="text-xs text-muted-foreground/60 mt-0.5 truncate leading-tight">
          {colDef.description}
        </p>
      </div>

      {/* Right-side controls */}
      <div className="shrink-0 flex items-center gap-3 ml-auto">
        <div className="w-16 flex justify-end">
          <Badge
            variant="accent"
            accent={colDef.category === 'Core' ? 'nile' : 'teal'}
            className="text-[10px] px-2 py-0 h-5 font-medium"
          >
            {colDef.category}
          </Badge>
        </div>
        <div className="w-9 flex items-center justify-center">
          {colDef.locked ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-muted-foreground/40 cursor-default">
                  <Lock className="h-3.5 w-3.5" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="text-xs">Always visible — required column</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Switch
              checked={isVisible}
              onCheckedChange={() => onToggle(colDef.key)}
              aria-label={`${isVisible ? 'Hide' : 'Show'} ${colDef.label} column`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Monthly hour bars (compact, inside expanded row) ─────────────────────────

function MonthlyHourBars({ hints }: { hints: Record<string, number> }) {
  const entries = Object.entries(hints);
  if (!entries.length) return null;
  const max = Math.max(...entries.map(([, h]) => h));
  return (
    <div className="flex items-end gap-2">
      {entries.map(([k, h]) => {
        const [yr, mo] = k.split('-').map(Number);
        const monthName = new Date(yr, mo - 1).toLocaleDateString('en-GB', { month: 'short' });
        const heightPct = Math.round((h / max) * 32);
        return (
          <TooltipProvider key={k} delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-1 cursor-default">
                  <span className="text-[10px] font-bold text-foreground/70 tabular-nums">{h}h</span>
                  <div
                    className="w-7 rounded-sm bg-primary/25 hover:bg-primary/40 transition-colors"
                    style={{ height: `${Math.max(6, heightPct)}px` }}
                  />
                  <span className="text-[10px] text-muted-foreground">{monthName}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {monthName}: {h}h
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// ── Expanded row detail ────────────────────────────────────────────────────────

// ── Budget health colour helpers (EDS semantic) ──────────────────────────────

const HEALTH_COLORS: Record<'teal' | 'warning' | 'destructive', {
  progress: string;
  text: string;
}> = {
  teal:        { progress: 'bg-[var(--accent-teal-500)]',        text: 'text-[var(--accent-teal-700)]' },
  warning:     { progress: 'bg-[var(--accent-warning-500)]',     text: 'text-[var(--accent-warning-700)]' },
  destructive: { progress: 'bg-destructive',                     text: 'text-destructive' },
};

// ── Lightweight budget summary for expanded row ──────────────────────────────

interface BudgetContextBriefProps {
  request: ResourceRequest;
  budgetCache: React.MutableRefObject<Map<string, EBProjectsBudgetComposite>>;
}

function BudgetContextBrief({ request, budgetCache }: BudgetContextBriefProps) {
  const [budgetData, setBudgetData] = useState<EBProjectsBudgetComposite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const key = String(request.projectId);
    const cached = budgetCache.current.get(key);
    if (cached) {
      setBudgetData(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    projectsApi.getBudgetsFromEBProjects(key)
      .then((res) => {
        if (cancelled) return;
        budgetCache.current.set(key, res);
        setBudgetData(res);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [request.projectId, budgetCache]);

  if (loading) {
    return (
      <div className="space-y-2 mt-4">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          <BarChart3 className="h-3 w-3" /> Budget Context
        </p>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-2 w-full" />
      </div>
    );
  }

  if (error || !budgetData) {
    return (
      <div className="mt-4">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
          <BarChart3 className="h-3 w-3" /> Budget Context
        </p>
        <span className="text-[10px] text-muted-foreground italic">No budget data available</span>
      </div>
    );
  }

  const matchedRole = findRoleBudget(budgetData.roleBudgets, request.role);
  if (!matchedRole) {
    return (
      <div className="mt-4">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
          <BarChart3 className="h-3 w-3" /> Budget Context
        </p>
        <span className="text-[10px] text-muted-foreground italic">No budget for {request.role} role</span>
      </div>
    );
  }

  const health = budgetHealthAccent(matchedRole.percentUsed);
  const colors = HEALTH_COLORS[health];

  return (
    <div className="mt-4">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <BarChart3 className="h-3 w-3" /> Budget Context — {matchedRole.roleName}
      </p>
      <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2 space-y-2">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[9px] text-muted-foreground">Budget</div>
            <div className="text-xs font-bold tabular-nums">{matchedRole.budgetHours}h</div>
          </div>
          <div>
            <div className="text-[9px] text-muted-foreground">Consumed</div>
            <div className="text-xs font-bold tabular-nums">{matchedRole.actualHours}h</div>
          </div>
          <div>
            <div className="text-[9px] text-muted-foreground">Remaining</div>
            <div className={cn('text-xs font-bold tabular-nums', colors.text)}>{matchedRole.remainingHours}h</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', colors.progress)}
              style={{ width: `${Math.min(100, matchedRole.percentUsed)}%` }}
            />
          </div>
          <span className={cn('text-[10px] font-bold tabular-nums', colors.text)}>
            {Math.round(matchedRole.percentUsed)}%
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Expanded row detail ────────────────────────────────────────────────────────

interface ExpandedDetailProps {
  request:        ResourceRequest;
  onViewPlan:     () => void;
  onDecline:      () => void;
  budgetCache:    React.MutableRefObject<Map<string, EBProjectsBudgetComposite>>;
  colSpan?:       number;
}

function ExpandedDetail({ request, onViewPlan, onDecline, budgetCache, colSpan = 8 }: ExpandedDetailProps) {
  const priorityCfg = PRIORITY_CONFIG[request.priority];
  const hasHints    = !!request.monthlyHourHints && Object.keys(request.monthlyHourHints).length > 0;

  return (
    <TableRow className="hover:bg-transparent border-b-0">
      <TableCell colSpan={colSpan} className="py-0 px-0">
        <div className={cn(
          'mx-4 mb-3 rounded-lg border-l-4 border border-border/50 bg-muted/15 overflow-hidden',
          priorityCfg.rowAccent,
        )}>

          {/* ── Body: 2-col layout ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-border/40">

            {/* Left col — metadata */}
            <div className="px-5 py-4 space-y-4">

              {/* Skills */}
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Tag className="h-3 w-3" /> Required Skills
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {request.skills.length > 0
                    ? request.skills.map(s => (
                        <Badge key={s} variant="secondary" className="text-xs px-2 py-0.5 font-normal">{s}</Badge>
                      ))
                    : <span className="text-xs text-muted-foreground italic">No skills specified</span>
                  }
                </div>
              </div>

              {/* Source + Location row */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                    <ExternalLink className="h-3 w-3" /> Requested by
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-foreground font-medium">{request.requestedByName}</span>
                  </div>
                </div>

                {request.location && (
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" /> Location
                    </p>
                    <span className="text-xs text-foreground/80">{request.location}</span>
                  </div>
                )}
              </div>

              {/* Notes */}
              {request.notes && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                    <MessageSquare className="h-3 w-3" /> Notes from requester
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed italic border-l-2 border-border pl-2.5">
                    "{request.notes}"
                  </p>
                </div>
              )}
            </div>

            {/* Right col — monthly spread + budget context */}
            <div className="px-5 py-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" /> Monthly Hour Spread
              </p>
              {hasHints
                ? <MonthlyHourBars hints={request.monthlyHourHints!} />
                : (
                  <div className="flex items-start gap-2.5 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-3 border border-dashed border-border/60">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">
                      No monthly breakdown provided. The planning engine will distribute hours optimally based on resource availability.
                    </span>
                  </div>
                )
              }

              {/* Budget Context */}
              <BudgetContextBrief request={request} budgetCache={budgetCache} />
            </div>
          </div>

          {/* ── Footer action strip ── */}
          {request.status !== 'declined' && (
            <div className="flex items-center gap-2 px-5 py-2.5 bg-muted/20 border-t border-border/40">
              {request.status === 'plan-ready' && (
                <Button size="sm" className="gap-1.5 text-xs h-7" onClick={onViewPlan}>
                  View Plan
                </Button>
              )}
              {request.status === 'accepted' && (
                <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={onViewPlan}>
                  <CheckCircle2 className="h-3 w-3" />
                  View Accepted Plan
                </Button>
              )}
              {(request.status === 'pending' || request.status === 'plan-ready') && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 ml-auto"
                  onClick={onDecline}
                >
                  Decline request
                </Button>
              )}
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Empty state ────────────────────────────────────────────────────────────────

function EmptyState({ filtered, onClear, colSpan = 8 }: { filtered: boolean; onClear: () => void; colSpan?: number }) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan}>
        <div className="flex flex-col items-center justify-center py-20 text-center px-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60 mb-4 border border-border/50">
            {filtered
              ? <Filter className="h-7 w-7 text-muted-foreground/50" />
              : <Inbox   className="h-7 w-7 text-muted-foreground/50" />
            }
          </div>
          <p className="text-sm font-semibold text-foreground">
            {filtered ? 'No matching requests' : 'No resource requests yet'}
          </p>
          <p className="text-xs text-muted-foreground mt-1.5 max-w-xs leading-relaxed">
            {filtered
              ? 'Clear your filters to see all requests.'
              : 'Requests from project managers in connected systems will appear here.'}
          </p>
          {filtered && (
            <Button variant="outline" size="sm" className="mt-4 gap-1.5 text-xs" onClick={onClear}>
              <Filter className="h-3.5 w-3.5" />
              Clear filters
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export interface ResourceRequestsTabProps {
  resources:             Resource[];
  existingBookings:      Booking[];
  /** Callback to notify the parent of the current demand count */
  onDemandCountChange?:  (count: number) => void;
  globalWorkingProfile:  WorkingHoursProfile;
  /** Called when the user wants to preview a plan scenario on the main calendar */
  onPreviewOnCalendar?:  (preview: PlanPreviewScenario) => void;
  /** When set, auto-opens the plan drawer for this request id (used when returning from calendar preview) */
  initialOpenRequestId?: string;
  /** Called once the initialOpenRequestId has been consumed so the parent can clear it */
  onInitialOpenRequestIdConsumed?: () => void;
}

export function ResourceRequestsTab({ resources, existingBookings, onDemandCountChange, globalWorkingProfile, onPreviewOnCalendar, initialOpenRequestId, onInitialOpenRequestIdConsumed }: ResourceRequestsTabProps) {

  // ── Priority thresholds (configurable in Settings → Open Demand) ──────────
  const thresholds = useRequestPriorityThresholds();
  const { currentCompany } = useCompany();
  const { isDemo } = useDataMode();
  const isDemoMode = isDemo('calendar');


  // ── Request data (fetched from Demands API in live mode, hardcoded in demo) ─
  const [requests, setRequests] = useState<ResourceRequest[]>([]);
  const [rawDemands, setRawDemands] = useState<Demand[]>([]);
  const [loadingState, setLoadingState] = useState<'loading' | 'loaded' | 'error'>('loading');

  const fetchDemands = useCallback(async () => {
    if (isDemoMode) {
      setRequests(getDemoResourceRequests());
      setRawDemands([]);
      setLoadingState('loaded');
      return;
    }
    if (!currentCompany?.id) {
      setRequests([]);
      setRawDemands([]);
      setLoadingState('loaded');
      return;
    }
    setLoadingState('loading');
    try {
      const demands = await demandsApi.getByCompany(currentCompany.id);
      setRawDemands(demands);
      setRequests(demandsToResourceRequests(demands));
      setLoadingState('loaded');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch demands';
      toast.error(message);
      setLoadingState('error');
    }
  }, [currentCompany?.id, isDemoMode]);

  useEffect(() => {
    if (isDemoMode) {
      setRequests(getDemoResourceRequests());
      setRawDemands([]);
      setLoadingState('loaded');
      return;
    }
    let cancelled = false;
    if (!currentCompany?.id) {
      setRequests([]);
      setRawDemands([]);
      setLoadingState('loaded');
      return;
    }
    setLoadingState('loading');
    demandsApi.getByCompany(currentCompany.id).then(demands => {
      if (cancelled) return;
      setRawDemands(demands);
      setRequests(demandsToResourceRequests(demands));
      setLoadingState('loaded');
    }).catch(err => {
      if (cancelled) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch demands';
      toast.error(message);
      setLoadingState('error');
    });
    return () => { cancelled = true; };
  }, [currentCompany?.id, isDemoMode]);

  // Notify parent of demand count changes
  useEffect(() => {
    onDemandCountChange?.(requests.filter(r => r.status === 'pending').length);
  }, [requests, onDemandCountChange]);

  // ── Budget cache (shared across expanded rows to avoid refetching) ────────
  const budgetCacheRef = useRef<Map<string, EBProjectsBudgetComposite>>(new Map());

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen]                 = useState(false);
  const [activeRequest, setActiveRequest]           = useState<ResourceRequest | null>(null);
  const [activeScenariosMap, setActiveScenariosMap] = useState<Record<string, PlanScenario[]>>({});
  const [isGenerating, setIsGenerating]             = useState(false);

  // ── Stable refs used by the auto-open effect (declared after handleGeneratePlan) ──
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const handleGeneratePlanRef = useRef<((request: ResourceRequest) => void) | null>(null);

  // ── Table state ───────────────────────────────────────────────────────────
  const [expandedRow, setExpandedRow]       = useState<string | null>(null);
  const [searchQuery, setSearchQuery]       = useState('');
  const [statusFilter, setStatusFilter]     = useState<StatusFilter>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [sortField, setSortField]           = useState<SortField>('priority');
  const [sortDir, setSortDir]               = useState<SortDir>('none');
  const [showAccepted, setShowAccepted]     = useState(false);

  // ── Column configuration state ─────────────────────────────────────────────
  const [rrVisibleColumns, setRrVisibleColumns] = useState<Set<RRColumnKey>>(new Set(RR_DEFAULT_VISIBLE));
  const [rrColumnOrder, setRrColumnOrder]       = useState<RRColumnKey[]>(RR_DEFAULT_ORDER);
  const [rrConfigOpen, setRrConfigOpen]         = useState(false);

  const rrVisibleCount = RR_ALL_COLUMNS.filter(c => rrVisibleColumns.has(c.key)).length;

  const rrToggleColumn = (key: RRColumnKey) => {
    const def = RR_ALL_COLUMNS.find(c => c.key === key);
    if (def?.locked) return;
    setRrVisibleColumns(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const rrResetColumns = () => {
    setRrVisibleColumns(new Set(RR_DEFAULT_VISIBLE));
    setRrColumnOrder(RR_DEFAULT_ORDER);
  };

  const rrHandleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setRrColumnOrder(prev => {
        const oldIdx = prev.indexOf(active.id as RRColumnKey);
        const newIdx = prev.indexOf(over.id as RRColumnKey);
        return arrayMove(prev, oldIdx, newIdx);
      });
    }
  };

  const rrDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Compute priority dynamically from thresholds ─────────────────────────
  // Priority on the stored request is now only a fallback for accepted/declined
  // requests where "days until start" is no longer a meaningful signal.
  const resolvedRequests = useMemo(() =>
    requests.map(r => ({
      ...r,
      priority: (r.status === 'accepted' || r.status === 'declined')
        ? r.priority                                          // keep original for closed requests
        : computeRequestPriority(r.requestedStartDate, thresholds),
    })),
  [requests, thresholds]);

  // ── Filtered + sorted ─────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const base = resolvedRequests.filter(r => {
      // Hide accepted by default unless the user has toggled them on
      if (!showAccepted && r.status === 'accepted') return false;
      if (q) {
        const hit =
          r.projectName.toLowerCase().includes(q) ||
          r.role.toLowerCase().includes(q) ||
          r.requestedByName.toLowerCase().includes(q) ||
          r.skills.some(s => s.toLowerCase().includes(q)) ||
          (r.projectCode?.toLowerCase().includes(q) ?? false);
        if (!hit) return false;
      }
      if (statusFilter   !== 'all' && r.status   !== statusFilter)   return false;
      if (priorityFilter !== 'all' && r.priority !== priorityFilter) return false;
      return true;
    });
    return sortRequests(base, sortField, sortDir);
  }, [resolvedRequests, searchQuery, statusFilter, priorityFilter, sortField, sortDir, showAccepted]);

  const isFiltered = !!(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all');

  // ── Sort handler ─────────────────────────────────────────────────────────
  // ── Sort handler — 3-state: asc → desc → none ────────────────────────────
  const handleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => {
          if (d === 'asc')  return 'desc';
          if (d === 'desc') return 'none';
          return 'asc';
        });
        return field;
      }
      setSortDir('asc');
      return field;
    });
  }, []);

  // ── Plan handlers ─────────────────────────────────────────────────────────
  const handleGeneratePlan = useCallback(async (request: ResourceRequest) => {
    setActiveRequest(request);
    setDrawerOpen(true);
    setIsGenerating(true);
    try {
      // generatePlanScenarios is now async (calls backend API for fresh availability)
      const scenarios = await generatePlanScenarios({ request, resources, existingBookings, globalWorkingProfile });
      setActiveScenariosMap(prev => ({ ...prev, [request.id]: scenarios }));
      setRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'plan-ready' as const } : r));
    } catch (err) {
      const description = err instanceof ApiError
        ? (err.details || err.getUserMessage())
        : 'An error occurred while analysing resources.';
      toast.error('Failed to generate plan', { description });
    } finally {
      setIsGenerating(false);
    }
  }, [resources, existingBookings, globalWorkingProfile]);

  // Keep the ref up-to-date so the auto-open effect can call it without stale closures
  handleGeneratePlanRef.current = handleGeneratePlan;

  // ── Auto-open drawer when returning from calendar preview ─────────────────
  useEffect(() => {
    if (!initialOpenRequestId) return;
    const target = requestsRef.current.find(r => r.id === initialOpenRequestId);
    if (!target) return;
    setActiveRequest(target);
    setDrawerOpen(true);
    setIsGenerating(false);
    if (!activeScenariosMap[target.id]) {
      handleGeneratePlanRef.current?.(target);
    }
    onInitialOpenRequestIdConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenRequestId, requests]);

  const handleViewPlan = useCallback((request: ResourceRequest) => {
    setActiveRequest(request);
    setDrawerOpen(true);
    setIsGenerating(false);
    if (!activeScenariosMap[request.id]) {
      handleGeneratePlan(request);
    }
  }, [activeScenariosMap, handleGeneratePlan]);

  const handleDecline = useCallback(async (requestId: string) => {
    if (isDemoMode) {
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'declined' as const } : r));
      toast.info('Request declined');
      return;
    }
    try {
      await demandsApi.updateStatus(Number(requestId), 'cancelled');
      await fetchDemands();
      toast.info('Request declined');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to decline demand';
      toast.error('Decline failed', { description: message });
    }
  }, [fetchDemands, isDemoMode]);

  const handleAccept = useCallback(async (scenarioId: string, slotEdits: Map<string, object>) => {
    if (!activeRequest) return;
    const scenario = activeScenariosMap[activeRequest.id]?.find(s => s.id === scenarioId);
    if (!scenario) return;

    if (isDemoMode) {
      setRequests(prev => prev.map(r =>
        r.id === activeRequest.id ? { ...r, status: 'accepted' as const, acceptedScenarioId: scenarioId } : r,
      ));
      const editCount = Array.from(slotEdits.values()).filter(e => Object.keys(e).length > 0).length;
      const editNote  = editCount > 0 ? ` (${editCount} booking${editCount !== 1 ? 's' : ''} adjusted)` : '';
      toast.success('Plan accepted — bookings created', {
        description: `${scenario.slots.length} booking${scenario.slots.length !== 1 ? 's' : ''} across ${scenario.resourceCount} resource${scenario.resourceCount !== 1 ? 's' : ''}${editNote}.`,
      });
      return;
    }

    const demandId = Number(activeRequest.id);
    const demand = rawDemands.find(d => d.id === demandId);
    const demandLines = demand?.demandLines ?? [];

    try {
      let totalBookingsCreated = 0;

      for (const slot of scenario.slots) {
        const edits = slotEdits.get(slot.id) as { resourceId?: string; hours?: number; startDate?: string; endDate?: string } | undefined;
        const effectiveResourceId = edits?.resourceId ?? slot.resourceId;

        // Match slot to a DemandLine by month key (YYYY-MM format)
        const matchedLine = findDemandLineByMonth(demandLines, slot.monthKey);
        const targetLine = matchedLine ?? demandLines.find(l => l.status !== 'filled');
        if (!targetLine) continue;

        const breakdown = slot.dailyBreakdown;
        if (!breakdown || breakdown.length === 0) {
          // No daily breakdown — fall back to daily auto-calculation on the backend
          const effectiveHours     = edits?.hours     ?? slot.hours;
          const effectiveStartDate = edits?.startDate ?? slot.startDate;
          const effectiveEndDate   = edits?.endDate   ?? slot.endDate;
          await demandsApi.allocateLineDaily(demandId, targetLine.id, {
            resourceId: Number(effectiveResourceId),
            totalHours: effectiveHours,
            startDate: effectiveStartDate,
            endDate: effectiveEndDate,
          });
          totalBookingsCreated++;
          continue;
        }

        // Create one booking per day, following the plan breakdown exactly
        for (const day of breakdown) {
          await demandsApi.allocateLine(demandId, targetLine.id, {
            resourceId: Number(effectiveResourceId),
            hours: day.hours,
            startDate: day.startTime,
            endDate: day.endTime,
          });
          totalBookingsCreated++;
        }
      }

      // Refresh from API — status is auto-calculated by the backend sproc
      await fetchDemands();

      const editCount = Array.from(slotEdits.values()).filter(e => Object.keys(e).length > 0).length;
      const editNote  = editCount > 0 ? ` (${editCount} booking${editCount !== 1 ? 's' : ''} adjusted)` : '';

      toast.success('Plan accepted — bookings created', {
        description: `${totalBookingsCreated} booking${totalBookingsCreated !== 1 ? 's' : ''} across ${scenario.resourceCount} resource${scenario.resourceCount !== 1 ? 's' : ''}${editNote}.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to allocate demand';
      toast.error('Allocation failed', { description: message });
    }
  }, [activeRequest, activeScenariosMap, rawDemands, fetchDemands, isDemoMode]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setShowAccepted(false);
  }, []);

  const toggleRow = useCallback((id: string) => {
    setExpandedRow(prev => prev === id ? null : id);
  }, []);

  // ── Export handler ────────────────────────────────────────────────────────
  const handleExportToExcel = useCallback(() => {
    const rows = filtered.map(r => ({
      'Priority':       PRIORITY_CONFIG[r.priority].label,
      'Project':        r.projectName,
      'Project Code':   r.projectCode ?? '',
      'Role':           r.role,
      'Hours':          r.totalHoursRequested,
      'Start Date':     fmtDate(r.requestedStartDate),
      'Status':         STATUS_CONFIG[r.status].label,
      'Requested By':   r.requestedByName,
      'Received':       fmtDate(r.receivedAt),
      'Skills':         r.skills.join(', '),
      'Location':       r.location ?? '',
      'Notes':          r.notes ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Resource Requests');

    // Auto-size columns
    const colWidths = Object.keys(rows[0] ?? {}).map(key => ({
      wch: Math.max(key.length, ...rows.map(r => String(r[key as keyof typeof r]).length)) + 2,
    }));
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, `resource-requests-${new Date().toISOString().slice(0, 10)}.xlsx`);

    toast.success('Export complete', {
      description: `${filtered.length} request${filtered.length !== 1 ? 's' : ''} exported to Excel.`,
    });
  }, [filtered]);

  const activeScenarios = activeRequest ? (activeScenariosMap[activeRequest.id] ?? []) : [];
  const acceptedCount   = resolvedRequests.filter(r => r.status === 'accepted').length;

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loadingState === 'loading') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading resource demands...</span>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (loadingState === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-destructive mb-3">Failed to load resource demands</p>
        <Button variant="outline" size="sm" onClick={fetchDemands}>
          Retry
        </Button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search project, role, skill…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm bg-card"
          />
        </div>

        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="h-9 w-[140px] text-sm bg-card">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="plan-ready">Plan Ready</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={v => setPriorityFilter(v as PriorityFilter)}>
          <SelectTrigger className="h-9 w-[140px] text-sm bg-card">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {(['critical', 'high', 'medium', 'low'] as RequestPriority[]).map(p => (
              <SelectItem key={p} value={p}>
                <span className="flex items-center gap-2">
                  <span
                    className={cn('h-2 w-2 rounded-full shrink-0', PRIORITY_CONFIG[p].dotAccent)}
                    style={{ backgroundColor: 'var(--accent-border)' }}
                  />
                  {PRIORITY_CONFIG[p].label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isFiltered && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground gap-1.5" onClick={handleClearFilters}>
            <Filter className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}

        {/* ── Divider + icon action buttons ── */}
        <div className="h-6 w-px bg-border shrink-0" />

        <TooltipProvider delayDuration={300}>
          {/* Export to Excel */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={handleExportToExcel}
                disabled={filtered.length === 0}
                aria-label="Export to Excel"
              >
                <FileSpreadsheet className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Export to Excel</TooltipContent>
          </Tooltip>

          {/* Configure Columns */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 relative"
                onClick={() => setRrConfigOpen(true)}
                aria-label="Configure columns"
              >
                <Settings2 className="h-4 w-4" />
                {rrVisibleCount < RR_ALL_COLUMNS.length && (
                  <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center leading-none">
                    {rrVisibleCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Configure columns ({rrVisibleCount} of {RR_ALL_COLUMNS.length} visible)</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Accepted toggle */}
        {acceptedCount > 0 && (
          <button
            onClick={() => setShowAccepted(v => !v)}
            className={cn(
              'ml-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition-colors',
              showAccepted
                ? 'bg-muted border-border text-foreground font-medium'
                : 'bg-transparent border-dashed border-border/60 text-muted-foreground hover:text-foreground hover:border-border',
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            {showAccepted ? 'Hide' : 'Show'} accepted
            <span className={cn(
              'inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] font-bold tabular-nums',
              showAccepted ? 'bg-foreground/10' : 'bg-muted',
            )}>
              {acceptedCount}
            </span>
          </button>
        )}

        {acceptedCount === 0 && (
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {filtered.length} of {resolvedRequests.length}
          </span>
        )}
      </div>

      {/* ── Table ── */}
      <div className="rounded-xl border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40 border-b">
              {/* Expand toggle — no label */}
              <TableHead className="w-8 px-3" />

              {rrColumnOrder.filter(k => rrVisibleColumns.has(k)).map(key => {
                const colDef = RR_ALL_COLUMNS.find(c => c.key === key)!;
                if (!colDef.sortField) return null;
                const colClassMap: Record<RRColumnKey, string> = {
                  priority:  'w-28 px-3',
                  project:   'px-3 min-w-[180px]',
                  role:      'w-44 px-3',
                  hours:     'w-24 px-3',
                  startDate: 'w-28 px-3',
                  status:    'w-32 px-3',
                };
                return (
                  <SortableHead
                    key={key}
                    field={colDef.sortField}
                    label={colDef.label}
                    currentField={sortField}
                    currentDir={sortDir}
                    onSort={handleSort}
                    className={colClassMap[key]}
                  />
                );
              })}

              {/* Actions — always visible */}
              <TableHead className="w-36 px-3 text-muted-foreground">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {filtered.length === 0 ? (
              <EmptyState filtered={isFiltered} onClear={handleClearFilters} colSpan={rrVisibleCount + 2} />
            ) : (
              filtered.map(request => {
                const statusCfg   = STATUS_CONFIG[request.status];
                const priorityCfg = PRIORITY_CONFIG[request.priority];
                const isExpanded  = expandedRow === request.id;
                const isDimmed    = request.status === 'declined';
                const receivedAgo = formatDistanceToNow(parseISO(request.receivedAt), { addSuffix: true });

                return (
                  <React.Fragment key={request.id}>
                    {/* ── Main row ── */}
                    <TableRow
                      className={cn(
                        'cursor-pointer transition-colors',
                        isExpanded && 'bg-muted/30 border-b-0',
                        isDimmed && 'opacity-50',
                      )}
                      onClick={() => toggleRow(request.id)}
                    >
                      {/* Expand chevron */}
                      <TableCell className="px-3 py-3 w-8">
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0',
                            isExpanded && 'rotate-90',
                          )}
                        />
                      </TableCell>

                      {/* Render cells in column order, respecting visibility */}
                      {rrColumnOrder.filter(k => rrVisibleColumns.has(k)).map(key => {
                        if (key === 'priority') return (
                          <TableCell key={key} className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn('h-2 w-2 rounded-full shrink-0', priorityCfg.dotAccent)}
                                style={{ backgroundColor: 'var(--accent-border)' }}
                              />
                              <Badge
                                variant="accent"
                                accent={priorityCfg.badgeAccent}
                                className="text-[10px] px-1.5 py-0 h-4 capitalize font-semibold"
                              >
                                {priorityCfg.label}
                              </Badge>
                            </div>
                          </TableCell>
                        );
                        if (key === 'project') return (
                          <TableCell key={key} className="px-3 py-3">
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="text-sm font-semibold leading-tight truncate max-w-[200px]">
                                {request.projectName}
                              </span>
                              {request.projectCode && (
                                <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {request.projectCode}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        );
                        if (key === 'role') return (
                          <TableCell key={key} className="px-3 py-3">
                            <span className="text-sm text-foreground/80 truncate block max-w-[160px]">
                              {request.role}
                            </span>
                          </TableCell>
                        );
                        if (key === 'hours') return (
                          <TableCell key={key} className="px-3 py-3">
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-sm font-bold tabular-nums cursor-default">
                                    {request.totalHoursRequested}h
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                  {hoursLabel(request.totalHoursRequested)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        );
                        if (key === 'startDate') return (
                          <TableCell key={key} className="px-3 py-3">
                            <div className="flex items-center gap-1.5 text-sm text-foreground/80">
                              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              {fmtDate(request.requestedStartDate)}
                            </div>
                          </TableCell>
                        );
                        if (key === 'status') return (
                          <TableCell key={key} className="px-3 py-3">
                            <Badge
                              variant="accent"
                              accent={statusCfg.accent}
                              className="gap-1 text-[10px] h-5 px-1.5"
                            >
                              {statusCfg.icon}
                              {statusCfg.label}
                            </Badge>
                          </TableCell>
                        );
                        return null;
                      })}

                      {/* Quick actions */}
                      <TableCell
                        className="px-3 py-3"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-1.5">
                          {request.status === 'pending' && (
                            <Button
                              size="sm"
                              className="h-7 gap-1 text-xs px-2.5"
                              onClick={() => handleGeneratePlan(request)}
                            >
                              Generate
                            </Button>
                          )}
                          {request.status === 'plan-ready' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 text-xs px-2.5 accent-info border-[var(--accent-border)] text-[var(--accent-text-on-outline)]"
                              onClick={() => handleViewPlan(request)}
                            >
                              View Plan
                            </Button>
                          )}
                          {request.status === 'accepted' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 gap-1 text-xs px-2.5 text-muted-foreground"
                              onClick={() => handleViewPlan(request)}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* ── Expanded detail row ── */}
                    {isExpanded && (
                      <ExpandedDetail
                        request={request}
                        onViewPlan={() => handleViewPlan(request)}
                        onDecline={() => handleDecline(request.id)}
                        budgetCache={budgetCacheRef}
                        colSpan={rrVisibleCount + 2}
                      />
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ── Table footer summary ── */}
      {filtered.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span>
            {filtered.filter(r => r.status === 'pending').length} pending ·{' '}
            {filtered.filter(r => r.status === 'plan-ready').length} plan ready ·{' '}
            {filtered.filter(r => r.status === 'accepted').length} accepted
          </span>
          <span className="tabular-nums">
            {filtered.reduce((s, r) => s + r.totalHoursRequested, 0)}h total hours
          </span>
        </div>
      )}

      {/* ── Plan Drawer ── */}
      <ResourceRequestPlanDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        request={activeRequest}
        resources={resources}
        existingBookings={existingBookings}
        scenarios={activeScenarios}
        isGenerating={isGenerating}
        onAccept={handleAccept}
        onPreviewOnCalendar={onPreviewOnCalendar}
      />

      {/* ── Configure Columns Sheet ── */}
      <Sheet open={rrConfigOpen} onOpenChange={setRrConfigOpen}>
        <SheetContent side="right" className="w-[440px] sm:max-w-[440px] flex flex-col p-0 gap-0">
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base font-semibold leading-tight">
                  Customise Columns
                </SheetTitle>
                <SheetDescription className="text-xs mt-0.5 leading-relaxed">
                  Toggle columns on or off, then drag to reorder them.
                </SheetDescription>
              </div>
            </div>

            {/* Visible count + reset */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold tabular-nums shrink-0">
                  {rrVisibleCount}
                </div>
                <span className="text-sm text-muted-foreground">
                  of {RR_ALL_COLUMNS.length} columns showing
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={rrResetColumns}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset defaults
              </Button>
            </div>
          </SheetHeader>

          {/* Drag hint */}
          <div className="px-6 py-3 bg-muted/30 border-b shrink-0">
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <GripVertical className="h-3.5 w-3.5 shrink-0" />
              Drag the handle on any row to reorder columns
            </p>
          </div>

          {/* Column list */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-4 py-4">
              <TooltipProvider delayDuration={300}>
                <DndContext
                  sensors={rrDndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={rrHandleDragEnd}
                >
                  <SortableContext items={rrColumnOrder} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {rrColumnOrder.map(key => {
                        const colDef = RR_ALL_COLUMNS.find(c => c.key === key)!;
                        return (
                          <RRSortableColumnRow
                            key={key}
                            colDef={colDef}
                            isVisible={rrVisibleColumns.has(key)}
                            onToggle={rrToggleColumn}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </TooltipProvider>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  );
}
